import type React from "react";

import type { SimpleProjectDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { XCloseIcon } from "@/components/Icons/XCloseIcon";
import { MultiLineContent } from "@/components/MultiLineContent/MultiLineContent";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { ProgressBar } from "@/components/ProgressBar/ProgressBar";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { SectionCard } from "@/design-system/SectionCard";
import { Spread } from "@/design-system/Spread";
import { useTranslation } from "@/translations";

import type { SurveySubmissionState } from "../CreateMassSurveyPage";

interface CreateMassSurveyProgressScreenProps {
  submissionState: SurveySubmissionState;
  progress: number;
  error?: string;
  projects: SimpleProjectDto[];
  onClose: () => void;
  onRetry: () => void;
}

export function CreateMassSurveyProgressScreen({
  submissionState,
  progress,
  error,
  projects,
  onClose,
  onRetry,
}: CreateMassSurveyProgressScreenProps): React.ReactNode {
  const { t } = useTranslation();

  const isCompleted = !submissionState.isSubmitting && submissionState.completed.length > 0;
  const isLoading = submissionState.isSubmitting;

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.mass-surveys.progress.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.mass-surveys.progress.title"),
            },
          ]}
        />
      }
    >
      <SectionCard>
        <PageGrid.Row>
          {isLoading && (
            <PageGrid.Column size="100%">
              <SectionCard>
                <section className="mx-auto flex max-w-measure-reading flex-col items-center gap-space-md py-space-xl">
                  <MultiLineContent align="center">
                    {t("page.mass-surveys.progress.creating", { count: submissionState.totalToSubmit })}
                  </MultiLineContent>
                  <div className="w-full max-w-control-narrow">
                    <ProgressBar progress={progress} />
                  </div>
                </section>
              </SectionCard>
            </PageGrid.Column>
          )}
          {error && (
            <PageGrid.Column size="100%">
              <SectionCard
                title={t("page.mass-surveys.progress.error.title")}
                description={error}
                icon={<XCloseIcon />}
              >
                <Inline gap="sm" wrap={false}>
                  <Button variant="subtle" tone="error" onClick={onClose}>
                    {t("common.action.cancel")}
                  </Button>
                  <Button onClick={onRetry}>{t("page.mass-surveys.progress.retry")}</Button>
                </Inline>
              </SectionCard>
            </PageGrid.Column>
          )}
          {isCompleted && submissionState.amountSuccess > 0 && (
            <PageGrid.Column size="100%">
              <SectionCard
                icon={<CheckIcon />}
                title={t("page.mass-surveys.progress.successful-surveys", {
                  count: submissionState.amountSuccess,
                })}
              >
                <p className="max-w-measure-reading">
                  {t("page.mass-surveys.progress.successful-surveys.description")}
                </p>
                <Button variant="soft" onClick={onClose}>
                  {t("page.mass-surveys.progress.close")}
                </Button>
              </SectionCard>
            </PageGrid.Column>
          )}
          {isCompleted && submissionState.amountFailure > 0 && (
            <>
              <PageGrid.Column size="100%">
                <h2 className="text-heading-2">{t("page.mass-surveys.progress.need-attention")}</h2>
              </PageGrid.Column>

              <PageGrid.Column size="50%">
                <SectionCard
                  icon={<XCloseIcon />}
                  title={t("page.mass-surveys.progress.failed-surveys", {
                    count: submissionState.amountFailure,
                  })}
                  description={t("page.mass-surveys.progress.failed-surveys.description")}
                >
                  <Inline gap="sm" wrap={false}>
                    <Button variant="soft" onClick={onClose}>
                      {t("page.mass-surveys.progress.close")}
                    </Button>
                    <Button onClick={onRetry}>{t("page.mass-surveys.progress.retry")}</Button>
                  </Inline>
                </SectionCard>
              </PageGrid.Column>

              <PageGrid.Column size="50%">
                <SectionCard title={t("page.mass-surveys.progress.detailed-results.failed")}>
                  {submissionState.completed
                    .filter((result) => result.status === "error")
                    .map((result) => {
                      const project = projects.find((p) => p.id === result.projectId);

                      return <Row key={result.projectId} project={project} isSuccess={false} />;
                    })}
                </SectionCard>
              </PageGrid.Column>
            </>
          )}
          {isCompleted && submissionState.amountSuccess > 0 && submissionState.amountFailure === 0 && (
            <PageGrid.Column size="100%">
              <SectionCard title={t("page.mass-surveys.progress.detailed-results")}>
                {submissionState.completed.map((result) => {
                  const project = projects.find((p) => p.id === result.projectId);

                  return (
                    <Row
                      key={result.projectId}
                      surveyId={result.surveyId}
                      project={project}
                      isSuccess={result.status === "success"}
                    />
                  );
                })}
                <footer className="mt-space-md">
                  <Button variant="soft" onClick={onClose}>
                    {t("page.mass-surveys.progress.close")}
                  </Button>
                </footer>
              </SectionCard>
            </PageGrid.Column>
          )}
        </PageGrid.Row>
      </SectionCard>
    </DocumentPage>
  );
}

function Row({
  project,
  isSuccess,
  surveyId,
}: {
  project?: SimpleProjectDto;
  isSuccess: boolean;
  surveyId?: string;
}): React.ReactNode {
  const { t } = useTranslation();

  return (
    <Card>
      <Spread>
        <span className="text-body-emphasis">{project?.name || "-"}</span>
        <Badge variant={isSuccess ? "success" : "danger"} icon={isSuccess ? <CheckIcon /> : <XCloseIcon />}>
          {isSuccess ? t("page.mass-surveys.progress.status.success") : t("page.mass-surveys.progress.status.failed")}
        </Badge>
      </Spread>
      {surveyId && (
        <span className="text-caption">{t("page.mass-surveys.progress.status.success.id", { id: surveyId })}</span>
      )}
    </Card>
  );
}
